import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import GTMContainer from './components/GTMContainer';

// Layout
import Layout from './components/Layout';
import ScrollToTop from './components/ScrollToTop';

// Pages
import Home from './pages/Home';
import Collection from './pages/Collection';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import CheckoutSuccess from './pages/CheckoutSuccess';
import OrderConfirmation from './pages/OrderConfirmation';
import Login from './pages/Login';
import Register from './pages/Register';
import MyOrders from './pages/MyOrders';
import ProRegister from './pages/ProRegister';
import AdminDashboard from './pages/AdminDashboard';
import Profile from './pages/Profile';
import { TurnstileProvider } from './components/Turnstile';
import About from './pages/About';
import Technology from './pages/Technology';
import Reviews from './pages/Reviews';
import FAQ from './pages/FAQ';
import APropos from './pages/APropos';
import Avis from './pages/Avis';
import Technologie from './pages/Technologie';
import Contact from './pages/Contact';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import PaymentPage from './pages/PaymentPage';
import PaymentSuccess from './pages/PaymentSuccess';
// Legal Pages
import MentionsLegales from './pages/MentionsLegales';
import CGV from './pages/CGV';
import CGU from './pages/CGU';
import PolitiqueRetour from './pages/PolitiqueRetour';
import PolitiqueConfidentialite from './pages/PolitiqueConfidentialite';
import PolitiqueCookies from './pages/PolitiqueCookies';

// Protected route component
function ProtectedRoute({ children, requireAdmin = false, requirePro = false }) {
  const { user, loading, isAdmin, isPro } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#00aaff] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user || user === false) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (requirePro && !isPro) {
    return <Navigate to="/espace-pro" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <>
    <ScrollToTop />
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Layout><Home /></Layout>} />
      <Route path="/collection" element={<Layout><Collection /></Layout>} />
      <Route path="/collection/:categorySlug" element={<Layout><Collection /></Layout>} />
      <Route path="/produit/:slug" element={<Layout><ProductDetail /></Layout>} />
      <Route path="/panier" element={<Layout><Cart /></Layout>} />
      <Route path="/checkout" element={<Layout><Checkout /></Layout>} />
      <Route path="/checkout/success" element={<Layout><CheckoutSuccess /></Layout>} />
      <Route path="/order-confirmation/:orderNumber" element={<Layout><OrderConfirmation /></Layout>} />
      <Route path="/login" element={<Layout><Login /></Layout>} />
      <Route path="/register" element={<Layout><Register /></Layout>} />
      <Route path="/espace-pro" element={<Layout><ProRegister /></Layout>} />
      <Route path="/demande-pro" element={<Layout><ProRegister /></Layout>} />
      <Route path="/a-propos" element={<Layout><APropos /></Layout>} />
      <Route path="/technologie" element={<Layout><Technologie /></Layout>} />
      <Route path="/avis" element={<Layout><Reviews /></Layout>} />
      <Route path="/faq" element={<Layout><FAQ /></Layout>} />
      
      {/* Legal routes */}
      <Route path="/mentions-legales" element={<Layout><MentionsLegales /></Layout>} />
      <Route path="/cgv" element={<Layout><CGV /></Layout>} />
      <Route path="/cgu" element={<Layout><CGU /></Layout>} />
      <Route path="/politique-retour" element={<Layout><PolitiqueRetour /></Layout>} />
      <Route path="/politique-confidentialite" element={<Layout><PolitiqueConfidentialite /></Layout>} />
      <Route path="/politique-cookies" element={<Layout><PolitiqueCookies /></Layout>} />
      
      {/* Contact */}
      <Route path="/contact" element={<Layout><Contact /></Layout>} />
      
      {/* Blog */}
      <Route path="/blog" element={<Layout><Blog /></Layout>} />
      <Route path="/blog/:slug" element={<Layout><BlogPost /></Layout>} />
      
      {/* Public Payment Routes (No auth required) */}
      <Route path="/payer/:token" element={<PaymentPage />} />
      <Route path="/paiement-reussi" element={<PaymentSuccess />} />
      
      {/* Protected routes */}
      <Route path="/mes-commandes" element={
        <ProtectedRoute>
          <Layout><MyOrders /></Layout>
        </ProtectedRoute>
      } />

      <Route path="/profil" element={
        <ProtectedRoute>
          <Layout><Profile /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin" element={
        <ProtectedRoute requireAdmin>
          <Layout><AdminDashboard /></Layout>
        </ProtectedRoute>
      } />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <TurnstileProvider>
        <BrowserRouter>
          <GTMContainer />
          <Toaster 
            position="top-center"
            toastOptions={{
              duration: 3000,
              style: {
                background: '#1a1a2e',
                color: '#fff',
                borderRadius: '12px',
              },
              success: {
                iconTheme: {
                  primary: '#00aaff',
                  secondary: '#fff',
                },
              },
            }}
          />
          <AppRoutes />
        </BrowserRouter>
        </TurnstileProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
